import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, BarChart3, TrendingUp, TrendingDown, Package, AlertTriangle, DollarSign } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import inventoryService, { InventoryStats } from '../services/inventoryService';

interface InventoryStatsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const InventoryStatsModal: React.FC<InventoryStatsModalProps> = ({ isOpen, onClose }) => {
  const { isDark } = useTheme();
  const { user } = useAuth();
  const { t } = useI18n();
  const [stats, setStats] = useState<InventoryStats | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && user) {
      loadStats();
    }
  }, [isOpen, user]);

  const loadStats = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      // Cargar estadísticas reales desde el servicio de inventario
      const realStats = await inventoryService.getInventoryStats();
      setStats(realStats);
    } catch (error) {
      console.error('Error cargando estadísticas:', error);
      // En caso de error, mostrar estadísticas básicas
      setStats({
        totalProducts: 0,
        totalValue: 0,
        lowStockProducts: 0,
        categoriesCount: 0,
        brandsCount: 0,
        suppliersCount: 0,
        lastUpdated: new Date() as any,
        topCategories: [],
        lowStockItems: [],
        recentActivity: []
      });
    } finally {
      setLoading(false);
    }
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'add':
        return <TrendingUp className="text-green-500" size={16} />;
      case 'update':
        return <Package className="text-blue-500" size={16} />;
      case 'delete':
        return <TrendingDown className="text-red-500" size={16} />;
      default:
        return <Package className="text-gray-500" size={16} />;
    }
  };

  const getActivityColor = (type: string) => {
    switch (type) {
      case 'add':
        return 'text-green-600 dark:text-green-400';
      case 'update':
        return 'text-blue-600 dark:text-blue-400';
      case 'delete':
        return 'text-red-600 dark:text-red-400';
      default:
        return 'text-gray-600 dark:text-gray-400';
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-50"
          />
          
          {/* Modal */}
          <motion.div
            key="modal"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className={`fixed inset-4 z-50 ${
              isDark ? 'bg-slate-900' : 'bg-white'
            } rounded-2xl shadow-2xl overflow-hidden`}
          >
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className={`flex items-center justify-between p-6 border-b ${
                isDark ? 'border-slate-700' : 'border-gray-200'
              }`}>
                <div className="flex items-center space-x-3">
                  <BarChart3 className={`${isDark ? 'text-red-400' : 'text-red-600'}`} size={24} />
                  <h2 className={`text-2xl font-bold ${
                    isDark ? 'text-white' : 'text-gray-900'
                  }`}>
                    {t('inventoryStatistics')}
                  </h2>
                </div>
                <button
                  onClick={onClose}
                  className={`p-2 rounded-xl transition-colors duration-200 ${
                    isDark 
                      ? 'hover:bg-slate-800 text-gray-300 hover:text-white' 
                      : 'hover:bg-gray-100 text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <X size={24} />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                {loading ? (
                  <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500"></div>
                  </div>
                ) : stats ? (
                  <div className="space-y-8">
                    {/* Summary Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                      <div className={`p-6 rounded-xl border ${
                        isDark 
                          ? 'bg-slate-800 border-slate-700' 
                          : 'bg-white border-gray-200'
                      } shadow-lg`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className={`text-sm font-medium ${
                              isDark ? 'text-gray-400' : 'text-gray-600'
                            }`}>
                              {t('totalProducts')}
                            </p>
                            <p className={`text-2xl font-bold ${
                              isDark ? 'text-white' : 'text-gray-900'
                            }`}>
                              {stats.totalProducts}
                            </p>
                          </div>
                          <Package className={`${isDark ? 'text-blue-400' : 'text-blue-600'}`} size={24} />
                        </div>
                      </div>

                      <div className={`p-6 rounded-xl border ${
                        isDark 
                          ? 'bg-slate-800 border-slate-700' 
                          : 'bg-white border-gray-200'
                      } shadow-lg`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className={`text-sm font-medium ${
                              isDark ? 'text-gray-400' : 'text-gray-600'
                            }`}>
                              {t('totalValue')}
                            </p>
                            <p className={`text-2xl font-bold ${
                              isDark ? 'text-white' : 'text-gray-900'
                            }`}>
                              ${stats.totalValue.toLocaleString()}
                            </p>
                          </div>
                          <DollarSign className={`${isDark ? 'text-green-400' : 'text-green-600'}`} size={24} />
                        </div>
                      </div>

                      <div className={`p-6 rounded-xl border ${
                        isDark 
                          ? 'bg-slate-800 border-slate-700' 
                          : 'bg-white border-gray-200'
                      } shadow-lg`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className={`text-sm font-medium ${
                              isDark ? 'text-gray-400' : 'text-gray-600'
                            }`}>
                              {t('lowStock')}
                            </p>
                            <p className={`text-2xl font-bold ${
                              isDark ? 'text-white' : 'text-gray-900'
                            }`}>
                              {stats.lowStockProducts}
                            </p>
                          </div>
                          <AlertTriangle className={`${isDark ? 'text-orange-400' : 'text-orange-600'}`} size={24} />
                        </div>
                      </div>

                      <div className={`p-6 rounded-xl border ${
                        isDark 
                          ? 'bg-slate-800 border-slate-700' 
                          : 'bg-white border-gray-200'
                      } shadow-lg`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className={`text-sm font-medium ${
                              isDark ? 'text-gray-400' : 'text-gray-600'
                            }`}>
                              {t('categories')}
                            </p>
                            <p className={`text-2xl font-bold ${
                              isDark ? 'text-white' : 'text-gray-900'
                            }`}>
                              {stats.categoriesCount}
                            </p>
                          </div>
                          <BarChart3 className={`${isDark ? 'text-purple-400' : 'text-purple-600'}`} size={24} />
                        </div>
                      </div>
                    </div>

                    {/* Top Categories */}
                    <div className={`p-6 rounded-xl border ${
                      isDark 
                        ? 'bg-slate-800 border-slate-700' 
                        : 'bg-white border-gray-200'
                    } shadow-lg`}>
                      <h3 className={`text-lg font-semibold mb-4 ${
                        isDark ? 'text-white' : 'text-gray-900'
                      }`}>
                        {t('categoriesWithMostInventory')}
                      </h3>
                      <div className="space-y-3">
                        {stats.topCategories.map((category, index) => (
                          <div key={index} className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                                isDark ? 'bg-slate-700 text-white' : 'bg-gray-100 text-gray-700'
                              }`}>
                                {index + 1}
                              </div>
                              <span className={`font-medium ${
                                isDark ? 'text-white' : 'text-gray-900'
                              }`}>
                                {category.name}
                              </span>
                            </div>
                            <div className="text-right">
                              <p className={`text-sm font-medium ${
                                isDark ? 'text-white' : 'text-gray-900'
                              }`}>
                                {category.count} {t('products')}
                              </p>
                              <p className={`text-xs ${
                                isDark ? 'text-gray-400' : 'text-gray-600'
                              }`}>
                                ${category.value.toLocaleString()}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Low Stock Alert */}
                    <div className={`p-6 rounded-xl border ${
                      isDark 
                        ? 'bg-slate-800 border-slate-700' 
                        : 'bg-white border-gray-200'
                    } shadow-lg`}>
                      <div className="flex items-center space-x-2 mb-4">
                        <AlertTriangle className={`${isDark ? 'text-orange-400' : 'text-orange-600'}`} size={20} />
                        <h3 className={`text-lg font-semibold ${
                          isDark ? 'text-white' : 'text-gray-900'
                        }`}>
                          {t('productsWithLowStock')}
                        </h3>
                      </div>
                      <div className="space-y-3">
                        {stats.lowStockItems.map((item, index) => (
                          <div key={index} className={`p-3 rounded-lg ${
                            isDark ? 'bg-slate-700' : 'bg-orange-50'
                          }`}>
                            <div className="flex items-center justify-between">
                              <div>
                                <p className={`font-medium ${
                                  isDark ? 'text-white' : 'text-gray-900'
                                }`}>
                                  {item.name}
                                </p>
                                <p className={`text-sm ${
                                  isDark ? 'text-gray-400' : 'text-gray-600'
                                }`}>
                                  SKU: {item.sku} • {item.category}
                                </p>
                              </div>
                              <div className={`px-3 py-1 rounded-full text-sm font-bold ${
                                item.stock === 1 
                                  ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                                  : 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400'
                              }`}>
                                {item.stock} unidades
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Recent Activity */}
                    <div className={`p-6 rounded-xl border ${
                      isDark 
                        ? 'bg-slate-800 border-slate-700' 
                        : 'bg-white border-gray-200'
                    } shadow-lg`}>
                      <h3 className={`text-lg font-semibold mb-4 ${
                        isDark ? 'text-white' : 'text-gray-900'
                      }`}>
                        {t('recentActivity')}
                      </h3>
                      <div className="space-y-3">
                        {stats.recentActivity.map((activity, index) => (
                          <div key={index} className="flex items-center space-x-3">
                            {getActivityIcon(activity.type)}
                            <div className="flex-1">
                              <p className={`text-sm font-medium ${
                                isDark ? 'text-white' : 'text-gray-900'
                              }`}>
                                {activity.action}
                              </p>
                              <p className={`text-xs ${
                                isDark ? 'text-gray-400' : 'text-gray-600'
                              }`}>
                                {activity.product}
                              </p>
                            </div>
                            <span className={`text-xs ${getActivityColor(activity.type)}`}>
                              {activity.date}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <BarChart3 className={`mx-auto mb-4 ${
                      isDark ? 'text-gray-600' : 'text-gray-400'
                    }`} size={48} />
                    <p className={`text-lg ${
                      isDark ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      No hay datos disponibles
                    </p>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default InventoryStatsModal;
